import { useState } from 'react';
import { Activity, Check, Sparkles } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Select,
} from '../../ui';
import { GOAL_STATUSES, statusLabel, statusTone } from '../../features/goals/goalModel';
import type { GoalHealthSignal } from '../../features/goals/goalHealth';
import type { GoalStatus } from '../../features/goals/types';

export interface GoalHealthPanelProps {
  health: GoalHealthSignal;
  currentStatus: GoalStatus;
  currentNote?: string;
  /** Persists a human status override (+ optional note). */
  onApply: (status: GoalStatus, note?: string) => Promise<unknown>;
}

/** Colour ramp for the health score bar. */
function scoreTone(score: number) {
  if (score >= 65) return 'bg-emerald-500';
  if (score >= 35) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Goal Health card. Surfaces the ADVISORY recommended signal + Vector
 * recommendation computed client-side, and always lets a human accept it or
 * override the effective status with a note. Nothing is applied automatically.
 */
export function GoalHealthPanel({
  health,
  currentStatus,
  currentNote,
  onApply,
}: GoalHealthPanelProps) {
  const [overrideStatus, setOverrideStatus] = useState<GoalStatus>(currentStatus);
  const [note, setNote] = useState(currentNote ?? '');
  const [busy, setBusy] = useState<'apply' | 'override' | null>(null);

  const apply = async (status: GoalStatus, n?: string) => {
    setBusy(status === health.recommended && n === undefined ? 'apply' : 'override');
    try {
      await onApply(status, n);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <Activity className="h-4 w-4 text-accent" />
            Goal Health
          </span>
        </CardTitle>
        <Badge tone="neutral">{health.confidence} confidence</Badge>
      </CardHeader>
      <CardBody className="space-y-4">
        {/* Recommended signal + score */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-zinc-500">
              Recommended
            </span>
            <Badge tone={statusTone(health.recommended)} dot>
              {statusLabel(health.recommended)}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Score</span>
            <span className="text-sm font-semibold tabular-nums text-zinc-100">
              {health.score}
            </span>
          </div>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
          <div
            className={`h-full rounded-full ${scoreTone(health.score)}`}
            style={{ width: `${health.score}%` }}
          />
        </div>

        {/* Vector recommendation */}
        <div className="rounded-xl border border-accent/30 bg-accent-soft/20 px-4 py-3">
          <p className="flex items-start gap-2 text-sm text-accent-fg">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{health.recommendation}</span>
          </p>
        </div>

        {/* Reasons */}
        {health.reasons.length > 0 && (
          <ul className="space-y-1 text-sm text-zinc-400">
            {health.reasons.map((r) => (
              <li key={r} className="flex gap-2">
                <span className="text-zinc-600">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Human override */}
        <div className="rounded-xl border border-surface-border bg-surface-1/50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-zinc-400">
              Effective status (human-owned)
            </p>
            <Badge tone={statusTone(currentStatus)}>{statusLabel(currentStatus)}</Badge>
          </div>

          {health.divergesFromStatus && (
            <Button
              size="sm"
              className="mb-3 w-full"
              leftIcon={<Check className="h-4 w-4" />}
              loading={busy === 'apply'}
              onClick={() => apply(health.recommended)}
            >
              Apply recommendation ({statusLabel(health.recommended)})
            </Button>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Select
              label="Override status"
              value={overrideStatus}
              options={GOAL_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
              onChange={(e) => setOverrideStatus(e.target.value as GoalStatus)}
            />
            <Input
              label="Note"
              placeholder="Why this status?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2"
            loading={busy === 'override'}
            onClick={() => apply(overrideStatus, note.trim() || undefined)}
          >
            Save override
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default GoalHealthPanel;
